<template>
  <div ref="el" class="token" :class="[role?.id, { unchecked: unchecked, right: isRight }]" @click="setRole">
    <RoleIcon :role="role" :player="player" :class="$attrs.class" />
    <span v-if="role?.firstNight || role?.firstNightReminder" class="leaf-left" />
    <span v-if="role?.otherNight || role?.otherNightReminder" class="leaf-right" />
    <span v-if="reminderLeaves" :class="['leaf-top' + reminderLeaves]" />
    <span v-if="role?.setup" class="leaf-orange" />
    <svg viewBox="0 0 150 150" class="name">
      <path id="curve" d="M 13 75 C 13 160, 138 160, 138 75" fill="transparent" />
      <text width="150" x="66.6%" text-anchor="middle" class="label mozilla" :font-size="nameToFontSize">
        <textPath xlink:href="#curve">
          {{ role?.name || "" }}
        </textPath>
      </text>
    </svg>
    <div class="edition" :class="[`edition-${role?.edition}`, role?.team]" />
    <div v-if="role?.ability" class="ability">
      {{ role?.ability }}
      <template v-if="role?.id === 'bootlegger' && grimoire.edition?.bootlegger">
        <div v-for="(rule, index) in grimoire.bootleggerRules" :key="index" class="rule">{{ rule }}</div>
      </template>
      <template v-if="role?.id === 'stormcatcher' && grimoire.edition?.stormcaught">
        <div class="rule">{{ grimoire.roles.get(grimoire.edition?.stormcaught)?.name }}</div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RoleIcon } from "@/components";
import { useGrimoireStore, useSessionStore, usePlayersStore } from "@/stores";
import type { Player, Role } from "@/types";

const grimoire = useGrimoireStore();
const session = useSessionStore();
const playersStore = usePlayersStore();

const el = ref<HTMLDivElement>();
const isRight = ref<boolean>(false);

const props = withDefaults(
  defineProps<{
    role?: Role | undefined;
    unchecked?: boolean;
    player?: Player;
  }>(),
  {
    role: () => ({}) as Role,
    unchecked: false,
    player: () => ({}) as Player,
  },
);

const emit = defineEmits<{
  "set-role": [role: Role];
}>();

const reminderLeaves = computed(() => {
  return (
    (props.role?.reminders || []).length +
    (props.role?.remindersGlobal || []).length
  );
});

const nameToFontSize = computed(() => {
  if (!props.role?.name) return "0%";
  if (props.role?.name.length <= 10) return "110%";
  return `${Math.max(110 - (props.role?.name.length - 10) * 3.75, 50)}%`;
});

onMounted(() => {
  isRight.value =
    el.value!.getBoundingClientRect().left +
    el.value!.getBoundingClientRect().width / 2 >
    window.innerWidth / 2;
});

function setRole() {
  if(grimoire.gamePhase !== 'postgame' || !session.isPlayerOrSpectator) {
    emit("set-role", props.role!);
    if(grimoire.gamePhase === 'postgame' && props.player) {
      playersStore.update({
        player: props.player,
        property: "secretRole",
        value: true
      });
    }
  }
}
</script>

<style scoped lang="scss">
.token {
  border-radius: 50%;
  width: 100%;
  background: url("../assets/token.png") center center;
  background-size: 100%;
  text-align: center;
  border: 3px solid var(--border-color);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 250ms transform 600s;

  &:hover .name .label {
    stroke: black;
    fill: white;

    @-moz-document url-prefix() {
      &.mozilla {
        stroke: none;
        filter: drop-shadow(0 1.5px 0 black) drop-shadow(0 -1.5px 0 black) drop-shadow(1.5px 0 0 black) drop-shadow(-1.5px 0 0 black) drop-shadow(0 2px 2px rgba(0, 0, 0, 0.5));
      }
    }
  }

  .icon,
  &:before {
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: center 30%;
    position: absolute;
    width: 100%;
    height: 100%;
    margin-top: 3%;
  }

  &.unchecked {
    background: #444;

    * {
      filter: grayscale(80%);
    }

    &:hover * {
      filter: grayscale(0%);
    }
  }

  span {
    position: absolute;
    width: 100%;
    height: 100%;
    background-size: 100%;
    pointer-events: none;

    &.leaf-left {
      background-image: url("../assets/leaf-left.png");
    }

    &.leaf-orange {
      background-image: url("../assets/leaf-orange.png");
    }

    &.leaf-right {
      background-image: url("../assets/leaf-right.png");
    }

    &.leaf-top1 {
      background-image: url("../assets/leaf-top1.png");
    }

    &.leaf-top2 {
      background-image: url("../assets/leaf-top2.png");
    }

    &.leaf-top3 {
      background-image: url("../assets/leaf-top3.png");
    }

    &.leaf-top4 {
      background-image: url("../assets/leaf-top4.png");
    }

    &.leaf-top5 {
      background-image: url("../assets/leaf-top5.png");
    }

    &.leaf-top6 {
      background-image: url("../assets/leaf-top6.png");
    }
  }

  picture {
    position: absolute;
    top: 0;
    width: 90%;
    height: 90%;
  }

  picture * {
    max-width: 100%;
    max-height: 100%;
    mix-blend-mode: multiply;
  }

  .name {
    width: 100%;
    height: 100%;
    font-size: 24px; // svg fonts are relative to document font size
    user-select: none;

    .label {
      fill: black;
      stroke: white;
      stroke-width: 2px;
      paint-order: stroke;
      font-family: "Papyrus", serif;
      font-weight: bold;
      text-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
      letter-spacing: 1px;

      @-moz-document url-prefix() {
        &.mozilla {
          // Vue doesn't support scoped media queries, so we have to use a second css class
          stroke: none;
          text-shadow: none;
          filter: drop-shadow(0 1.5px 0 white) drop-shadow(0 -1.5px 0 white) drop-shadow(1.5px 0 0 white) drop-shadow(-1.5px 0 0 white) drop-shadow(0 2px 2px rgba(0, 0, 0, 0.5));
        }
      }
    }
  }

  .edition {
    position: absolute;
    right: 0;
    bottom: 5px;
    width: 30px;
    height: 30px;
    background-size: 100%;
    display: none;
  }

  .ability {
    position: absolute;
    padding: 5px 10px;
    left: 120%;
    width: 250px;
    z-index: 25;
    font-size: 80%;
    background: #000a;
    border-radius: 10px;
    border: 3px solid var(--border-color);
    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.5));
    text-align: left;
    justify-items: center;
    align-content: center;
    align-items: center;
    pointer-events: none;
    opacity: 0;
    transition: opacity 200ms ease-in-out;

    &:before {
      content: " ";
      border: 10px solid transparent;
      width: 0;
      height: 0;
      border-right-color: black;
      position: absolute;
      margin-right: 2px;
      right: 100%;
    }

    .rule {
      margin-top: 1rem;

      &::before {
        content: '• ';
      }
    }
  }

  &:hover .ability {
    opacity: 1;
  }

  &.right .ability {
    left: auto;
    right: 120%;

    &:before {
      right: auto;
      left: 100%;
      border-left-color: black;
      border-right-color: transparent;
      position: absolute;
      margin-left: 2px;
    }
  }
}
</style>
